import { getAPIUrl } from "config/api";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { authSelector } from "store/slices/auth";
import styled from "styled-components";

import { RULE_ROOTS } from "./constants";
import { IRuleAttachment, RuleType } from "./types";

const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "bmp",
  "tif",
  "tiff",
]);

const isImageAttachment = (fileName: string): boolean => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  return !!ext && IMAGE_EXTENSIONS.has(ext);
};

const buildAttachmentUrl = (
  ruleType: RuleType,
  ruleId: string,
  attachmentId: string
): string => {
  const apiBase = getAPIUrl().replace(/\/+$/, "");
  return `${apiBase}/${RULE_ROOTS[ruleType]}/${ruleId}/attachments/${attachmentId}/download`;
};

const formatFileSize = (bytes: number): string => {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(2)} KB`;
};

const Container = styled.div`
  margin: 1rem 0 0.5rem;

  .p-panel .p-panel-header {
    cursor: pointer;
    user-select: none;
  }
`;

const LanguageBlock = styled.div`
  background: #f8f9fa;
  border-left: 3px solid #6366f1;
  padding: 0.625rem 0.875rem;
  margin: 0.5rem 0;
  white-space: pre-wrap;
  font-size: 13px;
  line-height: 1.5;
  color: #343a40;
`;

const ImageStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const ImagePreview = styled.img`
  display: block;
  width: 100%;
  max-width: 720px;
  max-height: 400px;
  object-fit: contain;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  cursor: pointer;
`;

const ImageSkeleton = styled.div`
  width: 100%;
  max-width: 720px;
  height: 200px;
  background: #f1f3f5;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 13px;
`;

const ImageFallback = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #f8f9fa;
  border: 1px dashed #ced4da;
  border-radius: 4px;
  color: #6c757d;
  font-size: 13px;
`;

const AttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

interface IRuleImagePreviewProps {
  ruleType: RuleType;
  ruleId: string;
  attachment: Pick<IRuleAttachment, "id" | "fileName">;
  token: string | null;
}

function RuleImagePreview({
  ruleType,
  ruleId,
  attachment,
  token,
}: IRuleImagePreviewProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let blobUrl: string | null = null;

    const load = async () => {
      try {
        const url = buildAttachmentUrl(ruleType, ruleId, attachment.id);
        const res = await fetch(url, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const blob = await res.blob();
        if (cancelled) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Inline image load failed", err);
        if (!cancelled) setErrored(true);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [ruleType, ruleId, attachment.id, token]);

  if (errored) {
    return (
      <ImageFallback>
        <i className="pi pi-image" />
        <span>{attachment.fileName} (failed to load)</span>
      </ImageFallback>
    );
  }
  if (!src) {
    return (
      <ImageSkeleton>
        <i className="pi pi-spin pi-spinner mr-2" />
        Loading {attachment.fileName}…
      </ImageSkeleton>
    );
  }
  return (
    <ImagePreview
      src={src}
      alt={attachment.fileName}
      onClick={() => window.open(src, "_blank", "noopener,noreferrer")}
    />
  );
}

const renderHeader = (options: {
  className: string;
  titleClassName: string;
  collapsed: boolean;
  onTogglerClick: (e: React.MouseEvent<HTMLElement>) => void;
  togglerElement: React.ReactNode;
}) => (
  <div
    className={options.className}
    onClick={options.onTogglerClick}
    role="button"
    tabIndex={0}
    aria-expanded={!options.collapsed}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        options.onTogglerClick(e as unknown as React.MouseEvent<HTMLElement>);
      }
    }}
  >
    <span className={options.titleClassName}>Relevant Contract Language</span>
    <span
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {options.togglerElement}
    </span>
  </div>
);

interface IRuleContractLanguageAccordionProps {
  ruleType: RuleType;
  ruleId: string;
  contractLanguage?: string | null;
  attachments?: IRuleAttachment[];
}

export function RuleContractLanguageAccordion({
  ruleType,
  ruleId,
  contractLanguage,
  attachments,
}: IRuleContractLanguageAccordionProps) {
  const { token } = useSelector(authSelector);
  const { current: toastElement } = useContext(ToastContext);

  const safeAttachments = attachments ?? [];
  const hasContent = !!contractLanguage || safeAttachments.length > 0;
  if (!hasContent) return null;

  const images = safeAttachments.filter((a) => isImageAttachment(a.fileName));
  const nonImages = safeAttachments.filter(
    (a) => !isImageAttachment(a.fileName)
  );

  const downloadAttachment = async (
    attachment: Pick<IRuleAttachment, "id" | "fileName">
  ) => {
    try {
      const url = buildAttachmentUrl(ruleType, ruleId, attachment.id);
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        throw new Error(
          `Download failed: ${response.status} ${response.statusText}`
        );
      }
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Attachment download failed", err);
      toastElement?.show({
        severity: "error",
        summary: "Download failed",
        detail:
          err instanceof Error ? err.message : "Failed to download attachment.",
      });
    }
  };

  return (
    <Container>
      <Panel toggleable collapsed headerTemplate={renderHeader}>
        {contractLanguage && <LanguageBlock>{contractLanguage}</LanguageBlock>}
        {images.length > 0 && (
          <ImageStack>
            {images.map((attachment) => (
              <RuleImagePreview
                key={attachment.id}
                ruleType={ruleType}
                ruleId={ruleId}
                attachment={attachment}
                token={token}
              />
            ))}
          </ImageStack>
        )}
        {nonImages.length > 0 && (
          <AttachmentList>
            {nonImages.map((attachment) => (
              <Button
                key={attachment.id}
                icon="pi pi-paperclip"
                label={`${attachment.fileName} (${formatFileSize(
                  attachment.fileSize
                )})`}
                className="p-button-outlined p-button-sm"
                onClick={() => downloadAttachment(attachment)}
              />
            ))}
          </AttachmentList>
        )}
      </Panel>
    </Container>
  );
}
